/* apps/web/src/components/ui/Icon.tsx
 *
 * The icon set drawn in the Claude Design canvases (`Yazamco Copilot
 * Redesign.dc.html` and `Composer.dc.html`).
 *
 * The design draws every icon as an inline 24×24 stroke SVG at a specific
 * weight — 1.6 for the quiet navigation glyphs, 1.8 for controls, 2+ for the
 * plus and the close cross. An icon font cannot express that, and the previous
 * implementation's FontAwesome glyphs had different silhouettes, different
 * optical sizes and no way to set stroke weight, so nothing lined up with the
 * canvas. These are the canvas paths, transcribed.
 *
 * `size` is the px box; `stroke` overrides the per-icon default weight where
 * the design uses a heavier one in a particular place.
 */
import type { CSSProperties } from "react";

export type IconName = keyof typeof PATHS;

interface Spec {
  /** Path `d` values, or a tuple describing a non-path primitive. */
  d: (string | { rect: [number, number, number, number]; rx?: number } | { circle: [number, number, number]; fill?: boolean })[];
  stroke?: number;
  /** Filled glyphs (the pin marker, the stop square, the overflow dots). */
  fill?: boolean;
}

const PATHS = {
  /* ---- navigation ---- */
  sparkle: {
    d: [
      "M12 3l1.6 4.8L18.4 9.4 13.6 11 12 15.8 10.4 11 5.6 9.4l4.8-1.6z",
      "M18 16.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z",
    ],
    stroke: 1.75,
  },
  /** The single-star form used at 13px inside a message avatar. */
  sparkleSolo: { d: ["M12 3l1.6 4.8L18.4 9.4 13.6 11 12 15.8 10.4 11 5.6 9.4l4.8-1.6z"], stroke: 1.85 },
  briefcase: {
    d: [{ rect: [3, 7, 18, 13] as [number, number, number, number], rx: 2 }, "M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"],
    stroke: 1.6,
  },
  gear: {
    d: [
      { circle: [12, 12, 3.1] as [number, number, number] },
      "M19.9 14.4a1.5 1.5 0 0 0 .3 1.65l.05.05a1.8 1.8 0 1 1-2.55 2.55l-.05-.05a1.5 1.5 0 0 0-1.65-.3 1.5 1.5 0 0 0-.9 1.37V20a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-.98-1.37 1.5 1.5 0 0 0-1.65.3l-.05.05A1.8 1.8 0 1 1 5.3 16.3l.05-.05a1.5 1.5 0 0 0 .3-1.65 1.5 1.5 0 0 0-1.37-.9H4a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1.37-.98 1.5 1.5 0 0 0-.3-1.65L5.12 7.4A1.8 1.8 0 1 1 7.67 4.85l.05.05a1.5 1.5 0 0 0 1.65.3H9.5a1.5 1.5 0 0 0 .9-1.37V4a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 .9 1.37 1.5 1.5 0 0 0 1.65-.3l.05-.05a1.8 1.8 0 1 1 2.55 2.55l-.05.05a1.5 1.5 0 0 0-.3 1.65v.02a1.5 1.5 0 0 0 1.37.9H20a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.37.9z",
    ],
    stroke: 1.6,
  },

  /* ---- chevrons + arrows ---- */
  chevronLeft: { d: ["M15 6l-6 6 6 6"], stroke: 1.85 },
  chevronRight: { d: ["M9 6l6 6-6 6"], stroke: 1.85 },
  chevronEnd: { d: ["M9 18l6-6-6-6"], stroke: 1.8 },
  chevronsUpDown: { d: ["M8 9l4-4 4 4M8 15l4 4 4-4"], stroke: 1.9 },
  arrowRight: { d: ["M5 12h13M12 5l7 7-7 7"], stroke: 1.9 },
  swap: { d: ["M4 8h13l-3.5-3.5M20 16H7l3.5 3.5"], stroke: 1.9 },

  /* ---- row + menu actions ---- */
  plus: { d: ["M12 5v14M5 12h14"], stroke: 2.1 },
  close: { d: ["M18 6 6 18M6 6l12 12"], stroke: 1.9 },
  dots: {
    d: [
      { circle: [12, 5, 1.6] as [number, number, number], fill: true },
      { circle: [12, 12, 1.6] as [number, number, number], fill: true },
      { circle: [12, 19, 1.6] as [number, number, number], fill: true },
    ],
    fill: true,
  },
  pencil: { d: ["M4 20h4L19 9l-4-4L4 16z"], stroke: 1.8 },
  pin: { d: ["M14.5 3.5 20.5 9.5l-2.2.5-3.8 3.8.8 3.8-1.4 1.4-3.7-3.7-3.9 3.9-1-1 3.9-3.9-3.7-3.7L7 9.2l3.8.8 3.8-3.8z"], stroke: 1.8 },
  pinFilled: { d: ["M14.5 2.5 21.5 9.5l-2.6.6-4.4 4.4.9 4.4-1.6 1.6-4.3-4.3-4.5 4.5-1.1-1.1 4.5-4.5-4.3-4.3 1.6-1.6 4.4.9 4.4-4.4z"], fill: true },
  archive: {
    d: [
      { rect: [3, 4, 18, 4] as [number, number, number, number], rx: 1 },
      "M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8",
      "M10 12h4",
    ],
    stroke: 1.8,
  },
  trash: { d: ["M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7"], stroke: 1.8 },
  copy: {
    d: [
      { rect: [9, 9, 11, 11] as [number, number, number, number], rx: 2 },
      "M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3",
    ],
    stroke: 1.8,
  },
  refresh: { d: ["M20 12a8 8 0 1 1-2.3-5.6", "M20 4v4h-4"], stroke: 1.8 },

  /* ---- account ---- */
  user: { d: [{ circle: [12, 8, 3.5] as [number, number, number] }, "M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6"], stroke: 1.8 },
  logout: { d: ["M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4", "M10 8l-4 4 4 4M6 12h9"], stroke: 1.8 },

  /* ---- workspace chrome ---- */
  search: { d: [{ circle: [11, 11, 7] as [number, number, number] }, "m20 20-4.3-4.3"], stroke: 1.9 },
  chat: { d: ["M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z"], stroke: 1.85 },
  export: { d: ["M12 15V4m0 0L8 8m4-4 4 4", "M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"], stroke: 1.8 },
  upload: { d: ["M12 16V4m0 0L8 8m4-4 4 4", "M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"], stroke: 1.7 },
  /** The "answer settings" slider glyph — also the composer's This-answer button. */
  sliders: {
    d: [
      "M4 7h10",
      "M18 7h2",
      { circle: [16, 7, 2] as [number, number, number] },
      "M4 17h4",
      "M12 17h8",
      { circle: [10, 17, 2] as [number, number, number] },
    ],
    stroke: 1.7,
  },
  doc: { d: ["M4 5h11l5 5v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z", "M15 5v5h5"], stroke: 1.8 },
  file: { d: ["M5 4h8l5 5v11H5z"], stroke: 1.9 },
  folder: { d: ["M4 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"], stroke: 1.7 },
  folderFlat: { d: ["M4 6a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"], stroke: 1.8 },

  /* ---- composer ---- */
  scope: { d: ["M3 7.5 12 3l9 4.5-9 4.5z", "M3 12l9 4.5 9-4.5", "M3 16.5 12 21l9-4.5"], stroke: 1.8 },
  paperclip: { d: ["M21 11.5 12 20.5a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7-7"], stroke: 1.8 },
  globe: { d: [{ circle: [12, 12, 9] as [number, number, number] }, "M3 12h18", "M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z"], stroke: 1.8 },
  building: { d: ["M4 21V8l8-4 8 4v13", "M4 21h16", "M9 21v-5h6v5", "M9 11h.01M15 11h.01"], stroke: 1.8 },
  brain: { d: ["M12 4a4 4 0 0 0-4 4 3 3 0 0 0-1 5.8V16a3 3 0 0 0 5 2.2A3 3 0 0 0 17 16v-2.2A3 3 0 0 0 16 8a4 4 0 0 0-4-4z", "M12 4v14"], stroke: 1.8 },
  stop: { d: [{ rect: [6, 6, 12, 12] as [number, number, number, number], rx: 2 }], fill: true },
  lock: { d: [{ rect: [5, 11, 14, 9] as [number, number, number, number], rx: 2 }, "M8 11V8a4 4 0 0 1 8 0v3"], stroke: 1.9 },
  lockLarge: { d: [{ rect: [3, 11, 18, 10] as [number, number, number, number], rx: 2 }, "M8 11V7a4 4 0 0 1 8 0v4"], stroke: 1.75 },

  /* ---- status ---- */
  check: { d: ["M5 12.5 10 17l9-10"], stroke: 2.2 },
  checkSmall: { d: ["M5 12l5 5 9-11"], stroke: 2 },
  info: { d: [{ circle: [12, 12, 9] as [number, number, number] }, "M12 11v5", { circle: [12, 7.6, 0.7] as [number, number, number], fill: true }], stroke: 1.7 },
  alertTriangle: { d: ["M12 3l9 16H3z", "M12 9v5", "M12 17h.01"], stroke: 1.75 },
  alertCircle: { d: ["M12 8v5", "M12 16.5h.01", { circle: [12, 12, 9] as [number, number, number] }], stroke: 1.9 },
} satisfies Record<string, Spec>;

interface IconProps {
  name: IconName;
  /** The px box. The canvas uses 11–18 depending on the surface. */
  size?: number;
  stroke?: number;
  className?: string;
  style?: CSSProperties;
  /** Given, the icon is exposed with this name instead of being hidden. */
  label?: string;
}

export function Icon({ name, size = 16, stroke, className, style, label }: IconProps): JSX.Element {
  const spec = PATHS[name] as Spec;
  const solid = spec.fill === true;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={solid ? "currentColor" : "none"}
      stroke={solid ? undefined : "currentColor"}
      strokeWidth={solid ? undefined : (stroke ?? spec.stroke ?? 1.8)}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
      style={{ flex: `0 0 ${size}px`, display: "block", ...style }}
    >
      {spec.d.map((part, index) => {
        if (typeof part === "string") return <path key={index} d={part} />;
        if ("rect" in part) {
          const [x, y, w, h] = part.rect;
          return <rect key={index} x={x} y={y} width={w} height={h} rx={part.rx} />;
        }
        const [cx, cy, r] = part.circle;
        return (
          <circle
            key={index}
            cx={cx}
            cy={cy}
            r={r}
            fill={part.fill ? "currentColor" : undefined}
            stroke={part.fill ? "none" : undefined}
          />
        );
      })}
    </svg>
  );
}

/**
 * The Microsoft four-square, drawn rather than fetched. The design builds it
 * from four coloured cells in a 16px grid — these are Microsoft's own brand
 * colours and are never re-tinted by the deployment brand.
 */
export function MicrosoftMark({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <span
      aria-hidden
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 2,
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
      }}
    >
      <i style={{ background: "#f25022" }} />
      <i style={{ background: "#7fba00" }} />
      <i style={{ background: "#00a4ef" }} />
      <i style={{ background: "#ffb900" }} />
    </span>
  );
}
